// Tasteful, contextual upgrade prompt. Renders nothing for active members,
// nothing while entitlement is still loading (no layout flash).
import { Link } from "@tanstack/react-router";
import { Sparkles, ArrowRight } from "lucide-react";
import { useEntitlement } from "@/hooks/use-entitlement";
import { track } from "@/lib/analytics";

type Props = {
  /** Where the prompt appears — used for conversion attribution. */
  context: "academy" | "activations" | "books" | "music" | "dashboard" | "app";
  title?: string;
  body?: string;
  className?: string;
};

const COPY: Record<Props["context"], { title: string; body: string }> = {
  academy: {
    title: "Open the full canon",
    body: "Membership unlocks master-tier lessons, certification tracks, and the deeper AI guide.",
  },
  activations: {
    title: "Never miss an activation",
    body: "Members receive every daily activation, the full archive, and downloadable practice audio.",
  },
  books: {
    title: "Read the complete editions",
    body: "Membership includes full-length readings, annotations, and early access to new works.",
  },
  music: {
    title: "Hear it first",
    body: "Members get early releases, unreleased songs, and members-only listening sessions.",
  },
  dashboard: {
    title: "Deepen your practice",
    body: "Membership adds daily activations, members-only teachings, and the full Academy canon.",
  },
  app: {
    title: "Unlock KAfrequency Membership",
    body: "One membership, honoured on web, iOS, and Android.",
  },
};

export function UpgradePrompt({ context, title, body, className = "" }: Props) {
  const { isMember, loading } = useEntitlement();
  if (loading || isMember) return null;
  const copy = COPY[context];

  return (
    <aside
      aria-labelledby={`upgrade-${context}-title`}
      className={`rounded-2xl border border-gold/40 bg-gold/5 p-6 ${className}`}
    >
      <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold">
        <Sparkles size={12} aria-hidden /> Membership
      </p>
      <h2 id={`upgrade-${context}-title`} className="mt-3 font-serif text-xl text-gold-luminous">
        {title ?? copy.title}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{body ?? copy.body}</p>
      <Link
        to="/membership"
        onClick={() => track("upgrade_prompt_click", { context })}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold"
      >
        See membership <ArrowRight size={14} aria-hidden />
      </Link>
    </aside>
  );
}
